// Detect TransferPitch CLUB / AGENCY profile URLs in message text so the chat
// renderer can stack an identity card under the bubble — the sibling of
// utils/profileLinkDetector.js (which owns player/coach). Kept separate so the
// player/coach detection stays byte-identical.
//
// Recognised flavours (same three as the profile detector):
//   1. Absolute apex / staging / dashboard host:
//      https://transferpitch.com/search/club/123
//      https://www.transferpitch.com/search/agency/45
//   2. localhost dev on any port:  http://localhost:3000/search/club/9
//   3. Relative address-bar paths: /search/club/123  ·  /search/agency/45

const ABS_TP_PATTERN =
  /https?:\/\/(?:www\.|staging\.|dashboard\.)?transferpitch\.com\/search\/(club|agency)\/(\w+)/gi;
const ABS_LOCAL_PATTERN =
  /https?:\/\/localhost(?::\d+)?\/search\/(club|agency)\/(\w+)/gi;
const REL_PATTERN = /(?<![\w/])\/search\/(club|agency)\/(\w+)(?![\w/])/gi;

/**
 * @typedef {Object} ClubLinkMatch
 * @property {"club"|"agency"} type
 * @property {string} id
 * @property {string} fullUrl   Exact substring matched in the message text.
 * @property {number} index
 */

/**
 * Find all TransferPitch club/agency links in a string. De-duplicates by
 * (type, id) so the same profile linked twice yields one card.
 * @param {string} text
 * @returns {ClubLinkMatch[]}
 */
export function detectClubLinks(text) {
  if (!text || typeof text !== "string") return [];
  const seen = new Set();
  const out = [];
  for (const re of [ABS_TP_PATTERN, ABS_LOCAL_PATTERN, REL_PATTERN]) {
    re.lastIndex = 0;
    let m;
    while ((m = re.exec(text)) !== null) {
      const type = m[1].toLowerCase(); // "club" | "agency"
      const id = m[2];
      const key = `${type}:${id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ type, id, fullUrl: m[0], index: m.index });
    }
  }
  out.sort((a, b) => a.index - b.index);
  return out;
}

/**
 * Cheap "does this text contain at least one club/agency link?" — lets the
 * renderer short-circuit without allocating an array for plain text.
 * @param {string} text
 * @returns {boolean}
 */
export function hasClubLink(text) {
  if (!text || typeof text !== "string") return false;
  if (!text.includes("/search/")) return false;
  ABS_TP_PATTERN.lastIndex = 0;
  ABS_LOCAL_PATTERN.lastIndex = 0;
  REL_PATTERN.lastIndex = 0;
  return (
    ABS_TP_PATTERN.test(text) ||
    ABS_LOCAL_PATTERN.test(text) ||
    REL_PATTERN.test(text)
  );
}
